import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { ChevronDown, ChevronUp, Volume2, StopCircle, Download, Mic, Sparkles, Presentation, HelpCircle } from 'lucide-react';
import RelatedVideosSection, { inferVideoTopic } from '../RelatedVideosSection';
import CompanionActions from './CompanionActions';
import CommentaryExcerpts from './CommentaryExcerpts';
import { LumeraPlusBadge, LockedDeeperTeaser } from './PremiumLock';
import { renderEmphasis } from './formatEmphasis';
import { downloadCompanionPdf, buildSermonFromConversation, sendConversationToSlidecon } from './companionConversationUtils';
import { recordSlideconFunnelEvent } from '../../lib/slideconFunnel';
import { toast } from 'sonner';

const ResponseView = ({
  language,
  currentResponse,
  conversationId,
  isPremium,
  isSpeaking,
  onSpeak,
  onStopSpeak,
  expandedSections,
  onToggleSection,
  lastQuestion,
  messagesEndRef,
  questionText,
  showToolbar = true,
  onRequirePremium = () => true,
}) => {
  const navigate = useNavigate();
  const es = language === 'es';
  // Runs `fn` for Premium users; otherwise opens the Lumera+ paywall.
  const gated = (label, fn) => () => {
    if (!isPremium) { onRequirePremium(label); return; }
    fn();
  };
  const lock = !isPremium && <LumeraPlusBadge className="ml-2" />;
  const [buildingSermon, setBuildingSermon] = React.useState(false);
  const [sendingToSlidecon, setSendingToSlidecon] = React.useState(false);
  const [showInfoHint, setShowInfoHint] = React.useState(false);

  // First-time auto-tooltip on the Slidecon `?` info button.
  // Fires once per browser for users who have already seen the v70 "Slidecon
  // is now included" toast (i.e. confirmed paid Slidecon-eligible users).
  // Auto-dismisses after 4.5s.
  React.useEffect(() => {
    if (!conversationId || !currentResponse || !isPremium || !showToolbar) return;
    if (typeof window === 'undefined' || !window.localStorage) return;
    // Only fire for users who have already seen the v70 unified-tier toast.
    // That flag is set ONLY for users with plan.includes_slidecon === true,
    // so it's a precise eligibility signal that doesn't depend on isPremium
    // (which currently reflects daily-question quota, not subscription tier).
    if (localStorage.getItem('lumera-slidecon-included-seen') !== '1') return;
    if (localStorage.getItem('lumera-slidecon-info-hint-seen') === '1') return;
    const showT = setTimeout(() => {
      setShowInfoHint(true);
      recordSlideconFunnelEvent('info_hint_seen');
    }, 1200);
    const hideT = setTimeout(() => {
      setShowInfoHint(false);
      try { localStorage.setItem('lumera-slidecon-info-hint-seen', '1'); } catch { /* ignore */ }
    }, 1200 + 4500);
    return () => { clearTimeout(showT); clearTimeout(hideT); };
  }, [conversationId, currentResponse, isPremium, showToolbar]);

  const handleBuildSermon = async () => {
    if (!conversationId || buildingSermon) return;
    setBuildingSermon(true);
    const draftId = await buildSermonFromConversation(conversationId);
    setBuildingSermon(false);
    if (draftId) {
      navigate(`/sermon-prep?draft_id=${encodeURIComponent(draftId)}`);
    }
  };

  const handleSendToSlidecon = async () => {
    if (!conversationId || sendingToSlidecon) return;
    recordSlideconFunnelEvent('slideshow_clicked');
    setSendingToSlidecon(true);
    const data = await sendConversationToSlidecon(conversationId);
    setSendingToSlidecon(false);
    if (data?.error) {
      if (data.upgradeRequired) {
        toast.error(data.error, {
          action: {
            label: language === 'es' ? 'Ver planes' : 'See plans',
            onClick: () => navigate('/pricing'),
          },
          duration: 8000,
        });
        return;
      }
      toast.error(data.error);
      return;
    }
    if (data?.edit_url) {
      window.open(data.edit_url, '_blank', 'noopener,noreferrer');
      toast.success(
        language === 'es'
          ? `Slidecon creó ${data.slide_count || 'algunas'} diapositivas`
          : `Slidecon built ${data.slide_count || 'your'} slides`,
      );
    }
  };
  if (!currentResponse) return null;

  const inferred = inferVideoTopic(
    [
      currentResponse?.understanding || '',
      currentResponse?.deeper_insight || '',
      currentResponse?.scripture?.primary_reference || '',
      ...(currentResponse?.scripture?.verses || []).map(
        (v) => `${v?.reference || ''} ${v?.text || ''}`,
      ),
      lastQuestion || '',
    ].join(' '),
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {questionText && (
        <div className="flex justify-end" data-testid="companion-user-question">
          <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-purple-600 text-white px-4 py-2 text-sm shadow">
            {questionText}
          </div>
        </div>
      )}
      {/* Voice + PDF + Build-Sermon Controls */}
      {showToolbar && (
      <div className="flex justify-end gap-2 flex-wrap">
        {conversationId && (
          <>
            <Button
              onClick={gated(es ? 'Crear sermón' : 'Build Sermon', handleBuildSermon)}
              disabled={buildingSermon}
              variant="default"
              size="sm"
              className="bg-gradient-to-r from-amber-600 to-orange-700 text-white hover:from-amber-700 hover:to-orange-800 disabled:opacity-60"
              data-testid="lumera-build-sermon-btn"
              title={language === 'es' ? 'Crear un sermón desde esta conversación' : 'Build a sermon from this conversation'}
            >
              <Mic className="w-4 h-4 mr-2" />
              {buildingSermon
                ? (language === 'es' ? 'Creando…' : 'Building…')
                : (language === 'es' ? 'Crear sermón' : 'Build Sermon')}
              {lock}
            </Button>
            <Button
              onClick={gated(es ? 'Exportar PDF' : 'PDF export', () => downloadCompanionPdf(conversationId))}
              variant="outline"
              size="sm"
              className="border-amber-600 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
              data-testid="lumera-download-pdf-btn"
              title={language === 'es' ? 'Descargar conversación como PDF' : 'Download conversation as PDF'}
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
              {lock}
            </Button>
            <Button
              onClick={gated(es ? 'Diapositivas' : 'Slideshow', handleSendToSlidecon)}
              disabled={sendingToSlidecon}
              variant="default"
              size="sm"
              className="bg-gradient-to-r from-fuchsia-600 to-violet-700 text-white hover:from-fuchsia-700 hover:to-violet-800 disabled:opacity-60"
              data-testid="lumera-slidecon-btn"
              title={language === 'es' ? 'Convertir en presentación con Slidecon' : 'Open as slideshow in Slidecon'}
            >
              <Presentation className="w-4 h-4 mr-2" />
              {sendingToSlidecon
                ? (language === 'es' ? 'Enviando…' : 'Sending…')
                : (language === 'es' ? 'Diapositivas' : 'Slideshow')}
              {lock}
            </Button>
            <span className="relative inline-flex">
              <Button
                onClick={() => {
                  setShowInfoHint(false);
                  try { localStorage.setItem('lumera-slidecon-info-hint-seen', '1'); } catch { /* ignore */ }
                  navigate('/profile#my-slidecon');
                }}
                variant="ghost"
                size="sm"
                className="text-fuchsia-600 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/30 px-2"
                data-testid="lumera-slidecon-info-btn"
                title={language === 'es' ? '¿Qué es Slidecon?' : 'What is Slidecon?'}
                aria-label={language === 'es' ? '¿Qué es Slidecon?' : 'What is Slidecon?'}
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
              {showInfoHint && (
                <span
                  data-testid="lumera-slidecon-info-hint"
                  className="pointer-events-none absolute top-full right-0 mt-2 z-50 whitespace-nowrap rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-1.5 shadow-lg animate-in fade-in slide-in-from-top-1"
                  role="status"
                >
                  {language === 'es'
                    ? 'Nuevo: toca ? para conocer Slidecon'
                    : 'New: tap ? to learn about Slidecon'}
                  <span className="absolute -top-1 right-3 w-2 h-2 rotate-45 bg-slate-900 dark:bg-slate-100" />
                </span>
              )}
            </span>
          </>
        )}
        {!isSpeaking ? (
          <Button
            onClick={gated(es ? 'Escuchar (voz IA)' : 'Listen (AI voice)', onSpeak)}
            variant="outline"
            size="sm"
            className="border-purple-600 text-purple-600 hover:bg-purple-50"
            data-testid="lumera-listen-btn"
          >
            <Volume2 className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Escuchar' : 'Listen'}
            {lock}
          </Button>
        ) : (
          <Button
            onClick={onStopSpeak}
            variant="outline"
            size="sm"
            className="border-red-600 text-red-600 hover:bg-red-50"
            data-testid="lumera-stop-listen-btn"
          >
            <StopCircle className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Detener' : 'Stop'}
          </Button>
        )}
      </div>
      )}

      {/* 1. Scripture */}
      <Card className="border-l-4 border-purple-600 shadow-lg">
        <CardHeader className="pb-3">
          <h3 className="text-sm font-semibold text-purple-600 dark:text-purple-400 flex items-center gap-2">
            <span>📖</span>
            {language === 'es' ? 'Escritura' : 'Scripture'}
          </h3>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentResponse.scripture?.verses?.map((verse, idx) => (
            <div key={idx} className="space-y-1">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                {verse.reference} ({String(verse.translation || '').replace(/^.*\(([^)]+)\)\s*$/, '$1')})
              </p>
              <p className="text-base text-slate-800 dark:text-slate-200 italic">
                "{verse.text}"
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 2. Understanding */}
      <Card className="shadow-md">
        <CardHeader className="pb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
            <span>💡</span>
            {language === 'es' ? 'Qué significa esto' : 'What this means'}
          </h3>
        </CardHeader>
        <CardContent>
          <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
            {renderEmphasis((currentResponse.understanding || '').replace(/^\s*(what this means|lo que esto significa|qué significa esto)\s*:?\s*/i, ''))}
          </p>
        </CardContent>
      </Card>

      {/* 2b. Topical videos */}
      <RelatedVideosSection language={language} topic={inferred} />

      {/* 3. Deeper Insight */}
      <Card className="shadow-md">
        <CardHeader
          className="pb-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
          onClick={() => onToggleSection('deeper')}
          data-testid="lumera-deeper-toggle"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span>🔍</span>
              {language === 'es' ? 'Profundizar' : 'Go deeper'}
              {lock}
            </h3>
            {expandedSections.deeper ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </CardHeader>
        {expandedSections.deeper && (
          <CardContent>
            {isPremium && !currentResponse.deeper_locked ? (
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed" data-testid="lumera-deeper-text">
                {renderEmphasis(currentResponse.deeper_insight)}
              </p>
            ) : (
              <LockedDeeperTeaser
                language={language}
                teaser={currentResponse.deeper_insight}
                onUnlock={() => onRequirePremium(es ? 'Profundizar' : 'Go deeper')}
              />
            )}
          </CardContent>
        )}
      </Card>

      {/* 3a-bis. Show-Reasoning chain — only renders when user toggled
          preferences.show_reasoning=true and the LLM responded with a
          `reasoning_steps` array. 3-5 short steps explaining HOW the
          companion arrived at its answer (context → parallels →
          commentators → application → caution). */}
      {Array.isArray(currentResponse.reasoning_steps) && currentResponse.reasoning_steps.length > 0 && (
        <Card
          className="border-l-4 border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/20 shadow-sm"
          data-testid="companion-reasoning"
        >
          <CardHeader className="pb-2">
            <h3 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {language === 'es' ? 'Mi razonamiento' : 'My reasoning'}
            </h3>
          </CardHeader>
          <CardContent>
            <ol className="space-y-1.5 text-xs">
              {currentResponse.reasoning_steps.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-2 text-slate-700 dark:text-slate-300"
                  data-testid={`companion-reasoning-step-${i}`}
                >
                  <span className="font-bold text-indigo-600 dark:text-indigo-400 shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* 3b. Matthew Henry public-domain commentary excerpt — auto-attached
          by the resourceful companion when the primary verse maps to an MH
          section. Falls through silently when the LLM didn't surface a
          mappable verse. */}
      <CommentaryExcerpts language={language} mhExcerpt={currentResponse.mh_excerpt} slot="mh" />

      {/* 4. Application */}
      <Card className="border-l-4 border-green-500 shadow-md">
        <CardHeader className="pb-3">
          <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
            <span>🙏</span>
            {language === 'es' ? 'Cómo aplicar esto hoy' : 'How to apply this today'}
          </h3>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {currentResponse.application?.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <span className="text-green-600 dark:text-green-400 font-semibold mt-0.5">•</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* 4b. Take-the-next-step action chips — clickable deep links into
          Lumera tools (Bible reader, Word Study, MH commentary, Sermon Prep,
          Games, Highlights, Preach Mode). Surfaced as a row right above the
          follow-up so the companion guides instead of just answering. */}
      {Array.isArray(currentResponse.actions) && currentResponse.actions.length > 0 && (
        <CompanionActions
          actions={currentResponse.actions}
          language={language}
          isPremium={isPremium}
          onRequirePremium={onRequirePremium}
        />
      )}

      {/* 4c. Verse Lens commentary snippets — short excerpts from up to 4
          public-domain commentaries (JFB, Clarke, Gill, TSK) for the
          primary verse. Renders only when the resourceful enrichment found
          at least one source. Tapping the header chips opens the full
          Verse Lens side panel for deeper reading. */}
      <CommentaryExcerpts language={language} verseLens={currentResponse.verse_lens} slot="lens" />

      {/* 4d. Follow-up question chips — 3 suggested next questions the user
          can tap to continue the conversation. The first is the most
          relevant; tapping it submits as a new question via the global
          companion event. */}
      {Array.isArray(currentResponse.follow_ups) && currentResponse.follow_ups.length > 0 && (
        <div className="space-y-2" data-testid="companion-followups">
          <p className="text-xs font-semibold uppercase tracking-widest text-pink-700 dark:text-pink-300">
            {language === 'es' ? 'Continuemos…' : 'Keep exploring'}
          </p>
          <div className="flex flex-wrap gap-2">
            {currentResponse.follow_ups.map((q, i) => (
              <button
                key={i}
                onClick={() => window.dispatchEvent(new CustomEvent('lumera:companion-ask', { detail: { question: q } }))}
                data-testid={`companion-followup-${i}`}
                className="px-3 py-1.5 rounded-full bg-pink-50 dark:bg-pink-950/40 border border-pink-200 dark:border-pink-800 text-xs text-pink-800 dark:text-pink-200 hover:bg-pink-100 dark:hover:bg-pink-900/40 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 5. Follow-Up */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900 border-none shadow-md">
        <CardContent className="pt-4">
          <p className="text-sm text-center text-slate-700 dark:text-slate-300 italic">
            ➡️ {currentResponse.follow_up}
          </p>
        </CardContent>
      </Card>

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ResponseView;
